import type { ColumnDef } from '@tanstack/react-table'
import Papa from 'papaparse'
import { useEffect, useMemo, useRef, useState } from 'react'
import { FBPage } from '../../components/FbUI/Page/FBPage'
import { FBPageTableContainer } from '../../components/FbUI/Page/styles'
import {
  Upload,
  Save,
  CheckCircle,
  FileUp,
  Download,
  ArrowLeft,
  X,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import FbButton from 'components/FbUI/FbButton'
import {
  createImportPreview,
  updateImportPreview,
  useGetImportPreview,
  performMatch,
  performImport,
} from './importer-api'
import { Select } from 'components/UI/Select/Select'
import {
  ColumnType,
  ColumnColors,
  ColumnOption,
  CSVRow,
  ImportData,
  DoorOption,
  ChainOption,
  CSVRowState,
  ImportType,
} from './importer-types'
import { ImporterDataTable } from './importer-datatable'
import { HelpTooltip } from 'components/Tooltip/HelpTooltip'
import { cn } from 'components/UI/cn'
import { Tabs } from 'components/FbUI/Tabs'
import { Switch } from 'components/UI/Switch/Switch'
import { useNavigate } from 'react-router-dom'
import { ManualSearchModal } from './ManualSearchModal'
import apiService from 'services/api'

export default function Importer(props: { importPreviewId: number | null }) {
  const navigate = useNavigate()
  const api = apiService()
  const { data: companyCategories } = api.useGetCompanyCategories()
  const {
    data: importPreview,
    isLoading,
    refetch,
  } = useGetImportPreview(props.importPreviewId)

  const [isUploading, setIsUploading] = useState(false)
  const [isMatching, setIsMatching] = useState(false)
  const [isImporting, setIsImporting] = useState(false)
  const [isClearingSuggestions, setIsClearingSuggestions] = useState(false)

  const [importData, setImportData] = useState<ImportData>()
  const [rawData, setRawData] = useState<Blob>()
  const [hasPendingChanges, setHasPendingChanges] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [selectedTab, setSelectedTab] = useState<CSVRowState>(
    'without_suggestions'
  )
  const [showManualSearch, setShowManualSearch] = useState(false)
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null)
  const [importType, setImportType] = useState<ImportType | null>(
    importPreview?.type ?? null
  )

  const companySubcategories = useMemo(() => {
    return companyCategories?.company_categories?.reduce(
      (acc, c) => {
        c.sub_categories?.forEach((s) => {
          acc.push({
            label: s.name,
            value: s.id,
            category_id: c.id,
          })
        })
        return acc
      },
      [] as { label: string; value: number; category_id: number }[]
    )
  }, [companyCategories])

  const mappedColumns = useMemo(() => {
    return importData?.headers.map((h) => h.mapped)
  }, [importData])

  /* -------------
  Field Handlers
  ------------- */
  function updateMap(original_header: string, mapped_column: string) {
    if (!importData) return
    const data = [...importData.headers]
    const header = data.find((h) => h.original === original_header)
    if (header) {
      header.mapped = mapped_column
    }
    setImportData({ ...importData, headers: data })
    setHasPendingChanges(true)
  }

  function updateMatchedDoor(
    row_id: string,
    matched_door: CSVRow['matched_door'],
    manual_match: boolean
  ) {
    console.log(matched_door, 'matched_door')
    if (!importData) return
    const data = [...importData.rows]
    const row = data.findIndex((r) => r.row_id === row_id)
    if (row !== -1) {
      data[row].matched_door = matched_door
      data[row].door_manual_match = manual_match
    }
    setImportData({ ...importData, rows: data })
    setHasPendingChanges(true)
  }

  function updateMatchedChain(
    row_id: string,
    matched_chain: CSVRow['matched_chain'],
    manual_match: boolean
  ) {
    console.log(matched_chain, 'matched_chain')
    if (!importData) return
    const data = [...importData.rows]
    const row = data.findIndex((r) => r.row_id === row_id)
    if (row !== -1) {
      data[row].matched_chain = matched_chain
      data[row].chain_manual_match = manual_match
    }
    setImportData({ ...importData, rows: data })
    setHasPendingChanges(true)
  }

  function updateIsCustomCompany(row_id: string, is_custom_company: boolean) {
    if (!importData) return
    const data = [...importData.rows]
    const row = data.findIndex((r) => r.row_id === row_id)
    if (row !== -1) {
      data[row].is_custom_company = is_custom_company
    }
    setImportData({ ...importData, rows: data })
    setHasPendingChanges(true)
  }

  function updateCustomCompanyCategory(
    row_id: string,
    category_id: number,
    subcategory_id: number | null
  ) {
    if (!importData) return
    const data = [...importData.rows]
    const row = data.findIndex((r) => r.row_id === row_id)
    if (row !== -1) {
      data[row].custom_company_category_id = category_id
      data[row].custom_company_sub_category_id = subcategory_id
    }
    setImportData({ ...importData, rows: data })
    setHasPendingChanges(true)
  }

  const startManualSearch = (rowId: string) => {
    setSelectedRowId(rowId)
    setShowManualSearch(true)
  }

  const handleManualSearchSelect = (
    door: DoorOption | null,
    chain: ChainOption | null
  ) => {
    if (!selectedRowId || !importData) return

    if (door) {
      updateMatchedDoor(selectedRowId, door, true)
      updateMatchedChain(selectedRowId, chain, true)
    } else if (chain) {
      updateMatchedChain(selectedRowId, chain, true)
    }

    setSelectedRowId(null)
    setShowManualSearch(false)
  }

  /* -------------
  Actions
  ------------- */
  async function handleSave() {
    if (!importData) {
      return
    }

    const dataToSave = { ...importData }

    // Update
    if (props.importPreviewId) {
      await updateImportPreview(props.importPreviewId, {
        import_data: {
          data: dataToSave,
        },
      })
      refetch()
    } else {
      // Create
      const rawDataString = await rawData?.text()
      const response = await createImportPreview({
        import_data: {
          data: dataToSave,
          raw_data: rawDataString ?? '',
        },
        type: importType ?? 'company_only',
      })
      navigate(`/importer/${response.id}`, { replace: true })
      return response.id
    }

    setImportData(dataToSave)
    setHasPendingChanges(false)
    return props.importPreviewId
  }

  async function handleMatch() {
    if (!importData) return
    if (!props.importPreviewId) return
    setIsMatching(true)
    try {
      await handleSave()
      const data = await performMatch(props.importPreviewId)
      setImportData(data)
    } catch (error) {
      console.error(error)
    } finally {
      setIsMatching(false)
      setHasPendingChanges(true)
    }
  }

  async function handleImport() {
    if (!importData) return
    if (!props.importPreviewId) return
    setIsImporting(true)
    try {
      await handleSave()
      const data = await performImport(props.importPreviewId)
      setImportData(data)
    } catch (error) {
      console.error(error)
    } finally {
      setIsImporting(false)
    }
  }

  async function handleClearSuggestions() {
    if (!importData) return
    setIsClearingSuggestions(true)
    const newData = { ...importData }
    const newRows = newData.rows.map((row) => {
      return {
        ...row,
        matched_door: null,
        matched_chain: null,
        suggested_doors: null,
        suggested_chains: null,
        ready: row.is_custom_company ? row.ready : false,
      }
    })
    setImportData({ ...newData, rows: newRows })
    setIsClearingSuggestions(false)
  }

  function handleExport() {
    if (!importData) return

    // Get ready rows
    const readyRows = importData.rows.filter((row) => row.ready)

    // Transform rows to include exploded matched fields
    const transformedRows = readyRows.map((row) => {
      const baseRow: Record<string, any> = {}
      // Remove nested objects
      for (const key in row) {
        if (
          [
            'matched_chain',
            'matched_door',
            'suggested_chains',
            'suggested_doors',
          ].includes(key)
        ) {
          continue
        }
        baseRow[key] = row[key]
      }

      // Add exploded matched chain fields
      if (row.matched_chain) {
        Object.entries(row.matched_chain).forEach(([key, value]) => {
          baseRow[`matched_chain__${key}`] = value
        })
      }

      // Add exploded matched door fields
      if (row.matched_door) {
        Object.entries(row.matched_door).forEach(([key, value]) => {
          baseRow[`matched_door__${key}`] = value
        })
      }

      return baseRow
    })

    // Convert to CSV
    const csv = Papa.unparse(transformedRows)

    // Create and trigger download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', 'exported_data.csv')
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  function handleFileClick() {
    fileInputRef.current?.click()
  }

  function handleFileUpload(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    setRawData(file)
    if (!file) return

    setIsUploading(true)
    Papa.parse<CSVRow>(file, {
      header: true,
      skipEmptyLines: true,
      delimitersToGuess: [';', ','],
      complete: (results) => {
        const data: CSVRow[] = results.data.map((row) => {
          return {
            ...Object.fromEntries(
              Object.entries(row).map(([key, value]) => [
                key,
                typeof value === 'string' ? value.slice(0, 200) : undefined,
              ])
            ),
            row_id: crypto.randomUUID(),
            ready: false,
            matched_door: null,
            matched_chain: null,
            suggested_doors: null,
            suggested_chains: null,
            door_manual_match: false,
            chain_manual_match: false,
            is_custom_company: false,
            chain_proxy_id: null,
            custom_company_category_id: null,
            custom_company_sub_category_id: null,
          }
        })
        const importData = {
          rows: data,
          headers:
            results.meta.fields?.map((h) => ({
              original: h,
              mapped: 'ignore',
            })) ?? [],
          type: importType ?? 'company_only',
        }
        setImportData(importData)
        setIsUploading(false)
        setHasPendingChanges(true)
      },
      error: (error) => {
        console.error('Error parsing CSV:', error)
        setIsUploading(false)
      },
    })
  }

  useEffect(() => {
    if (importPreview) {
      setImportType(importPreview.type)
      setImportData(importPreview.data)
    }

    return () => {
      setImportType(null)
      setImportData(undefined)
    }
  }, [importPreview])

  const columns = useMemo(() => {
    if (!importData) return []

    const headers = importData.headers

    const columnDefs: ColumnDef<CSVRow>[] = headers.map((header) => ({
      accessorKey: header.original,
      size: 300,
      header: () => {
        const mappedColumn = header.mapped
        console.log(mappedColumn, 'mappedColumn')
        return (
          <div>
            <div className="flex flex-row gap-2">
              <Select
                buttonSize="sm"
                onSelect={(value) => updateMap(header.original, value)}
                selected={header.mapped}
                items={Object.entries(
                  columnOptionsImportType[importType ?? 'company_only']
                ).map(([k, v]) => ({
                  label: v.name,
                  value: k,
                  hidden: mappedColumns?.includes(k),
                }))}
              />
              <FbButton
                variant="outline"
                size="sm"
                onClick={() => updateMap(header.original, 'ignore')}
              >
                <X size={16} />
              </FbButton>
            </div>
            <pre className="text-xs">{header.original}</pre>
          </div>
        )
      },
      cell: (info) => {
        const mappedColumn = header.mapped
        const columnType = (allColumnOptions[mappedColumn]?.type ??
          'ignore') as ColumnType
        const color = columnTypeColors[columnType]
        return (
          <td
            key={info.cell.id}
            className="text-sm max-h-[50px] h-[60px] max-w-[200px] w-[200px] truncate px-3 border"
            style={{
              backgroundColor: color.background,
              color: color.text,
            }}
          >
            <div>{info.getValue() as React.ReactNode}</div>
          </td>
        )
      },
    }))

    // MATCHES SELECTOR
    columnDefs.unshift(
      {
        accessorKey: 'matched_chain',
        header: 'Matched Chain',
        cell: (info) => {
          const row = info.row.original
          return (
            <td
              key={info.cell.id}
              className="text-sm max-h-[50px] h-[60px] max-w-[400px] w-[400px] truncate px-3 border"
            >
              <div
                className={cn(
                  'flex-1 flex flex-row gap-2 items-center justify-start',
                  {
                    'opacity-20': row.is_custom_company,
                  }
                )}
              >
                {row.matched_chain && (
                  <HelpTooltip
                    content={<ChainInfo chain={row.matched_chain} />}
                    delay={[0, 100]}
                  />
                )}
                {row.chain_manual_match ? (
                  <div className="text-sm truncate">
                    {row.matched_chain?.name}
                  </div>
                ) : (
                  <Select
                    buttonSize="sm"
                    className="w-[400px] max-h-[500px]"
                    buttonVariant={
                      row.matched_chain?.item_id ? 'primary' : 'outline'
                    }
                    disabled={
                      !row.suggested_chains?.length || row.is_custom_company
                    }
                    nullSelectionLabel={
                      row.suggested_chains?.length
                        ? `Found ${row.suggested_chains?.length} chain suggestions`
                        : 'No Suggestions'
                    }
                    items={row.suggested_chains?.map((chain) => ({
                      label: <ChainInfo chain={chain} />,
                      value: chain.item_id,
                      labelWhenSelected: (
                        <div className="flex flex-row gap-2">
                          <div className="text-sm truncate">{chain.name}</div>
                        </div>
                      ),
                    }))}
                    onSelect={(value) => {
                      const chain = row.suggested_chains?.find(
                        (c) => c.item_id === value
                      )
                      if (chain) {
                        updateMatchedChain(row.row_id, chain, false)
                      }
                    }}
                    selected={row.matched_chain?.item_id}
                  />
                )}
                <FbButton
                  variant="outline"
                  disabled={row.is_custom_company}
                  size="sm"
                  onClick={() => {
                    updateMatchedChain(row.row_id, null, false)
                    updateMatchedDoor(row.row_id, null, false)
                  }}
                >
                  Clear
                </FbButton>
              </div>
            </td>
          )
        },
      },
      {
        accessorKey: 'matched_door',
        header: 'Matched Door',
        cell: (info) => {
          const row = info.row.original
          const doors = row.suggested_doors?.filter((c) => {
            if (row.matched_chain) {
              return c.chain_id === row.matched_chain.item_id
            }
            return true
          })
          return (
            <td
              key={info.cell.id}
              className="text-sm max-h-[50px] h-[60px] max-w-[400px] w-[400px] truncate px-3 border"
            >
              <div
                className={cn(
                  'flex-1 flex flex-row gap-2 items-center justify-start',
                  {
                    'opacity-20': row.is_custom_company,
                  }
                )}
              >
                {row.matched_door && (
                  <HelpTooltip
                    content={<DoorInfo door={row.matched_door} />}
                    delay={[0, 100]}
                  />
                )}
                {row.door_manual_match || row.chain_manual_match ? (
                  <div className="text-sm truncate">
                    {row.matched_door?.name ?? 'No Door Selected'}
                  </div>
                ) : (
                  <Select
                    buttonSize="sm"
                    buttonVariant={
                      row.matched_door?.item_id ? 'primary' : 'outline'
                    }
                    className="w-[400px] max-h-[500px]"
                    disabled={
                      !row.suggested_doors?.length || row.is_custom_company
                    }
                    nullSelectionLabel={
                      row.matched_chain?.item_id
                        ? `${row.suggested_doors?.length} doors for selected chain`
                        : row.suggested_doors?.length
                          ? `Found ${row.suggested_doors?.length} door suggestions`
                          : 'No Suggestions'
                    }
                    items={doors?.map((door) => ({
                      label: <DoorInfo door={door} />,
                      labelWhenSelected: (
                        <div className="flex flex-row gap-2">
                          <div className="text-sm truncate">{door.name}</div>
                        </div>
                      ),
                      value: door.item_id,
                    }))}
                    onSelect={(value) => {
                      const door = row.suggested_doors?.find(
                        (d) => d.item_id === value
                      )
                      if (door) {
                        const chain =
                          row.suggested_chains?.find(
                            (c) => c.item_id === door?.chain_id
                          ) ?? null
                        updateMatchedDoor(row.row_id, door, false)
                        updateMatchedChain(row.row_id, chain, false)
                      }
                    }}
                    selected={row.matched_door?.item_id}
                  />
                )}
                <FbButton
                  variant="outline"
                  disabled={row.is_custom_company}
                  size="sm"
                  onClick={() => updateMatchedDoor(row.row_id, null, false)}
                >
                  Clear
                </FbButton>
                <FbButton
                  variant="outline"
                  size="sm"
                  onClick={() => startManualSearch(row.row_id)}
                >
                  Manual Search
                </FbButton>
              </div>
            </td>
          )
        },
      },
      {
        header: 'Custom',
        cell: (info) => {
          const row = info.row.original
          return (
            <td
              key={info.cell.id}
              className="text-sm max-h-[50px] h-[60px] max-w-[50px] w-[50px] min-w-0 truncate px-3 border"
            >
              <Switch
                checked={row.is_custom_company}
                onChange={(e) => {
                  updateIsCustomCompany(row.row_id, e.target.checked)
                }}
                labelRight="Custom"
              />
              <Select
                buttonSize="sm"
                disabled={!row.is_custom_company}
                items={companySubcategories}
                selected={row.custom_company_sub_category_id}
                onSelect={(value, originalItem) =>
                  updateCustomCompanyCategory(
                    row.row_id,
                    originalItem?.category_id,
                    value
                  )
                }
              />
            </td>
          )
        },
      }
    )

    return columnDefs ?? []
  }, [importData])

  const data = useMemo<Record<CSVRowState, CSVRow[]>>(() => {
    if (!importData)
      return {
        without_suggestions: [],
        with_suggestions: [],
        ready: [],
      }

    const withoutSuggestions = importData.rows.filter(
      (r) =>
        !r.suggested_doors?.length && !r.suggested_chains?.length && !r.ready
    )
    const withSuggestions = importData.rows.filter(
      (r) =>
        (r.suggested_doors?.length || r.suggested_chains?.length) && !r.ready
    )
    const ready = importData.rows.filter((r) => r.ready)

    return {
      without_suggestions: withoutSuggestions,
      with_suggestions: withSuggestions,
      ready: ready,
    }
  }, [importData, selectedTab])

  if (isUploading || isLoading) {
    return <div>Loading...</div>
  }

  if (!importType) {
    return (
      <FBPage>
        <div className="flex flex-col gap-2">
          <h1 className="text-2xl font-bold">Select Import Type</h1>
          <FbButton
            onClick={() => setImportType('company_only')}
            variant="primary"
          >
            Company Only
          </FbButton>
          <FbButton onClick={() => setImportType('tag')} variant="primary">
            Tag
          </FbButton>
        </div>
      </FBPage>
    )
  }

  return (
    <FBPage>
      <div className="flex items-center gap-4">
        <Link to="/importer">
          <FbButton
            onClick={(e) => {
              if (hasPendingChanges) {
                if (
                  !confirm(
                    'You have unsaved changes. Are you sure you want to go back?'
                  )
                ) {
                  e.preventDefault()
                  e.stopPropagation()
                }
              }
            }}
            variant="secondary"
            className="flex items-center gap-2"
          >
            <ArrowLeft size={16} />
            Go Back
          </FbButton>
        </Link>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          onChange={handleFileUpload}
          className="hidden"
        />
        <FbButton
          onClick={handleFileClick}
          variant="outline"
          className="flex items-center gap-2"
        >
          <FileUp size={16} />
          Upload CSV
        </FbButton>
        <FbButton
          onClick={handleSave}
          variant="primary"
          className="flex items-center gap-2"
          disabled={!hasPendingChanges}
        >
          <Save size={16} />
          Save
        </FbButton>
        <FbButton
          disabled={!props.importPreviewId}
          onClick={handleMatch}
          variant="warning"
          className="flex items-center gap-2"
          loading={isMatching}
        >
          <CheckCircle size={16} />
          Save & Get Suggestions
        </FbButton>
        <FbButton
          disabled={!props.importPreviewId}
          onClick={handleClearSuggestions}
          variant="warning"
          className="flex items-center gap-2"
          loading={isClearingSuggestions}
        >
          <CheckCircle size={16} />
          Clear Suggestions
        </FbButton>
        {selectedTab === 'ready' && (
          <div className="flex flex-row gap-2">
            <FbButton
              onClick={handleImport}
              variant="secondary"
              className="flex items-center gap-2"
              loading={isImporting}
            >
              <Upload size={16} />
              Save & Import Ready
            </FbButton>
            <FbButton
              onClick={handleExport}
              variant="secondary"
              className="flex items-center gap-2"
            >
              <Download size={16} />
              Export
            </FbButton>
          </div>
        )}
      </div>

      <div className="flex flex-row gap-2">
        <Tabs
          tabs={[
            {
              key: 'without_suggestions',
              title: `Without Suggestions (${data.without_suggestions.length})`,
            },
            {
              key: 'with_suggestions',
              title: `With Suggestions (${data.with_suggestions.length})`,
            },
            {
              key: 'ready',
              title: `Ready (${data.ready.length})`,
            },
          ]}
          selectedTab={selectedTab}
          setSelectedTab={(tab) => {
            setSelectedTab(tab as 'with_suggestions' | 'without_suggestions')
          }}
        />
      </div>

      <FBPageTableContainer
        style={{
          height: 'calc(var(--base-page-table-height) - 50px)',
        }}
      >
        <ImporterDataTable data={data[selectedTab]} columns={columns} />
      </FBPageTableContainer>

      <ManualSearchModal
        show={showManualSearch}
        onClose={() => {
          setShowManualSearch(false)
          setSelectedRowId(null)
        }}
        onSelect={handleManualSearchSelect}
      />
    </FBPage>
  )
}

function DoorInfo(props: { door: DoorOption }) {
  const { door } = props
  return (
    <div className="flex flex-col gap-1">
      <div className="font-bold">{door.name}</div>
      <div className="text-xs flex flex-col gap-1">
        <div>
          {door.score} - {door.address}
        </div>
        <div>
          {door.city}, {door.state} {door.zipcode} {door.country}
        </div>
        {door.domain && <div>Domain: {door.domain}</div>}
        <div>
          Chain: {door.chain_name} ({door.chain_size} locations)
        </div>
        {door.type && <div>Type: {door.type}</div>}
      </div>
    </div>
  )
}

function ChainInfo(props: { chain: ChainOption }) {
  const { chain } = props
  return (
    <div className="flex flex-col gap-1">
      <div className="font-bold">{chain.name}</div>
      {/* <div className="text-xs">{chain.chain_size} locations</div> */}
    </div>
  )
}

const columnTypeColors: Record<ColumnType, ColumnColors> = {
  // Field that is used to match the company exactly, rendering other matching fields irrelevant
  primary_key: {
    background: '#E3F2FD',
    text: '#1E40AF',
  },
  // Field that is used to match the company
  company_match: {
    background: '#F3E5F5',
    text: '#6B21A8',
  },
  // Regular data
  object_data: {
    background: '#ffffff',
    text: '#000000',
  },
  // Ignore column
  ignore: {
    background: 'transparent',
    text: '#4B556340',
  },
}

const baseColumnOptions: Record<string, ColumnOption> = {
  firstbite_id: {
    name: 'First Bite Chain ID',
    type: 'primary_key',
  },
  door_id: {
    name: 'Door ID',
    type: 'primary_key',
  },
  company_name: {
    name: 'Company Name',
    type: 'company_match',
  },
  company_phone: {
    name: 'Company Phone',
    type: 'company_match',
  },
  company_address_state: {
    name: 'Company State',
    type: 'company_match',
  },
  company_address_city: {
    name: 'Company City',
    type: 'company_match',
  },
  company_address_zip: {
    name: 'Company Zip',
    type: 'company_match',
  },
  company_address_country: {
    name: 'Company Country',
    type: 'company_match',
  },
  company_address_street: {
    name: 'Company Address',
    type: 'company_match',
  },
  company_address_street_2: {
    name: 'Company Address 2',
    type: 'company_match',
  },
  company_domain: {
    name: 'Company Domain',
    type: 'company_match',
  },
  company_full_address: {
    name: 'Company Full Address',
    type: 'company_match',
  },
}

const tagColumnOptions: Record<string, ColumnOption> = {
  tag_name: {
    name: 'Tag Name',
    type: 'object_data',
  },
}

const allColumnOptions = {
  ...baseColumnOptions,
  ...tagColumnOptions,
}

// Reference: server/importengine/engine.py -> ColumnType enum
const columnOptionsImportType: Record<
  ImportType,
  Record<string, ColumnOption>
> = {
  company_only: baseColumnOptions,
  tag: {
    ...baseColumnOptions,
    ...tagColumnOptions,
  },
}
